/**
 * Homepage — orchardoffers.com (agent-facing).
 *
 * Agents are the primary audience for the marketing site. The investor-facing
 * page lives at `/investors`.
 *
 * Sections: hero → 4-stat credibility bar → seller scenarios → agent success
 * stories → why-agents-use-us → how it works → markets → CTA.
 *
 * Copy is an AI-generated first draft pending marketing/product review
 * (see MRG-320 acceptance criteria).
 */

import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useAuth } from '@/common/hooks/useAuth'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import { Text } from '@/common/components/text'
import { MARKETS, ACTIVE_MARKET_COUNT } from '@/common/markets'
import * as styles from './index.css'

const STATS = [
  { value: '500+', label: 'vetted buyers in the marketplace' },
  { value: '4,500+', label: 'cash offers delivered this year' },
  { value: '100%', label: 'of your commission is still yours' },
  { value: '24 hrs', label: 'most homes see their first offer' },
]

const SELLER_SCENARIOS = [
  {
    icon: '/images/agents/icons/need-to-move-fast.svg',
    label: 'Need to move fast',
  },
  {
    icon: '/images/agents/icons/need-certainty.svg',
    label: 'Need certainty',
  },
  {
    icon: '/images/agents/icons/home-needs-work.svg',
    label: 'Home needs work',
  },
  {
    icon: '/images/agents/icons/big-life-change.svg',
    label: 'Big life change',
  },
]

const FEATURED_STORY = {
  eyebrow: 'From submission to close in 11 days',
  quote:
    'My client got a sudden job transfer and needed to be out of state in two weeks. Orchard Offers saved the deal.',
  attribution: 'Listing agent · Atlanta, Georgia',
  timeline: [
    {
      day: 'Day 1',
      title: 'Home submitted to Orchard Offers',
      body: 'Quick submission with photos and details.',
    },
    {
      day: 'Day 3',
      title: 'Competing offers in hand',
      body: 'Reviewed offers with her client and guided through all the details.',
    },
    {
      day: 'Day 4',
      title: 'Best offer accepted, contract signed',
      body: 'Above expected value, close date chosen by seller.',
    },
    {
      day: 'Day 11',
      title: 'Property closed',
      body: 'Seller relocated with a week to spare.',
      highlight: true,
    },
  ],
}

const TESTIMONIALS = [
  {
    quote: '12 interested buyers on a home that never hit the market.',
    attribution: 'Listing agent · Atlanta, Georgia',
  },
  {
    quote: 'We beat every iBuyer and still secured a leaseback for my client.',
    attribution: 'Listing agent · Dallas-Fort Worth',
  },
  {
    quote: "Closed in 11 days, with two competing offers, at $24K above the seller's target.",
    attribution: 'Listing agent · Dallas-Fort Worth',
  },
]

const WHY_AGENTS = [
  {
    title: 'Be the agent with more to offer',
    body: 'Give sellers more options without sending them elsewhere, so you can keep your clients in your pipeline.',
  },
  {
    title: 'Your client, your commission',
    body: 'Stay at the center of the sale as the agent of record. Guide your client from submission to close and keep your full commission.',
  },
  {
    title: 'No upfront cost or commitment',
    body: 'No upfront cost, no exclusivity, no commitment. Access is funded by participating investors.',
  },
]

const STEPS = [
  {
    title: "Submit your client's property",
    body: 'Enter the home details once through Orchard Offers. Homes submitted cannot be actively listed on MLS, except in Washington, where state rules require an MLS listing.',
  },
  {
    title: 'We source competing cash offers',
    body: 'National iBuyers and local investors bid on the property within the same timeline. Every offer is delivered directly to your inbox.',
  },
  {
    title: 'Advise your client and close',
    body: 'Walk your client through the options and help them choose the best path.',
  },
]

/**
 * "Submit a property" routes to two different places depending on auth:
 * - Authenticated agents (and any logged-in user) → /properties/create
 *   (mirrors the authenticated navbar's Submit-a-property action).
 * - Logged-out visitors → /request-account?role=agent so they can sign up.
 *
 * Without this branch, the previous unconditional /request-account?role=agent
 * sent logged-in agents through the public signup flow instead of the
 * property submission form.
 */
function SubmitPropertyLink({
  isAuthenticated,
  className,
  children,
}: {
  isAuthenticated: boolean
  className?: string
  children: ReactNode
}) {
  if (isAuthenticated) {
    return (
      <Link to="/properties/create" className={className}>
        {children}
      </Link>
    )
  }
  return (
    <Link to="/request-account" search={{ role: 'agent' as const }} className={className}>
      {children}
    </Link>
  )
}

// 5-star rating glyph. Single SVG, 5 stars at 24px with 6px gaps → 144px wide.
// Sky (#A7E9F1) per the Figma — ties to the eyebrow/attribution accent color.
function FiveStars({ className }: { className?: string }) {
  const star = 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'
  return (
    <svg
      width="144"
      height="24"
      viewBox="0 0 144 24"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="5 out of 5 stars"
      className={className}
    >
      {[0, 30, 60, 90, 120].map((x) => (
        <path key={x} d={star} transform={`translate(${x} 0)`} fill="#A7E9F1" />
      ))}
    </svg>
  )
}

export const Route = createFileRoute({
  component: HomePage,
})

function HomePage() {
  const { isAuthenticated } = useAuth()

  const scrollToHowItWorks = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <img
          src="/images/orchard-offers.svg"
          alt="Orchard Offers"
          className={styles.logo}
        />
        <nav className={styles.nav}>
          <Link to="/investors" className={styles.navLink}>
            For Investors
          </Link>
          <a
            href="#how-it-works"
            className={styles.navLink}
            onClick={(e) => {
              e.preventDefault()
              scrollToHowItWorks()
            }}
          >
            How it works
          </a>
          {isAuthenticated ? (
            <Link to="/properties">
              <Button variant="primary">
                <Icon name="dashboard" size={18} />
                Go to Dashboard
              </Button>
            </Link>
          ) : (
            <SubmitPropertyLink isAuthenticated={isAuthenticated}>
              <Button variant="primary">Submit a property</Button>
            </SubmitPropertyLink>
          )}
        </nav>
      </header>

      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroContent}>
            <Text as="h1" variant="h1" className={styles.heroTitle}>
              Get competitive cash offers for your clients
            </Text>
            <Text variant="bodyLg" className={styles.heroSubtitle}>
              Be the agent who delivers more. Orchard Offers puts your client's
              property in front of 500+ local and national investors, creating
              real competition and stronger cash offers.
            </Text>
            <div className={styles.heroCta}>
              <SubmitPropertyLink isAuthenticated={isAuthenticated} className={styles.heroCtaLink}>
                <Button variant="primary" size="md">
                  Submit a property
                </Button>
              </SubmitPropertyLink>
              <Button
                variant="outline"
                size="md"
                onClick={scrollToHowItWorks}
                className={styles.heroCtaButton}
              >
                Learn More
              </Button>
            </div>
            <Text variant="bodyXs" className={styles.heroEligibility}>
              Open to any licensed agent in the {ACTIVE_MARKET_COUNT} markets we serve.
            </Text>
          </div>
          <div className={styles.heroImageWrapper}>
            <img
              src="https://res.cloudinary.com/dmxgostd9/image/upload/f_auto,q_auto/dpr_2/w_500/v1778608115/web-assets/homepage/localized-homes/austin-tx-2.jpg"
              srcSet={[
                'https://res.cloudinary.com/dmxgostd9/image/upload/f_auto,q_auto/dpr_1/w_370/v1778608115/web-assets/homepage/localized-homes/austin-tx-2.jpg 370w',
                'https://res.cloudinary.com/dmxgostd9/image/upload/f_auto,q_auto/dpr_1/w_500/v1778608115/web-assets/homepage/localized-homes/austin-tx-2.jpg 500w',
                'https://res.cloudinary.com/dmxgostd9/image/upload/f_auto,q_auto/dpr_2/w_370/v1778608115/web-assets/homepage/localized-homes/austin-tx-2.jpg 740w',
                'https://res.cloudinary.com/dmxgostd9/image/upload/f_auto,q_auto/dpr_2/w_500/v1778608115/web-assets/homepage/localized-homes/austin-tx-2.jpg 1000w',
                'https://res.cloudinary.com/dmxgostd9/image/upload/f_auto,q_auto/dpr_3/w_500/v1778608115/web-assets/homepage/localized-homes/austin-tx-2.jpg 1500w',
              ].join(', ')}
              sizes="(max-width: 768px) 100vw, 500px"
              alt="Modern home exterior"
              className={styles.heroImage}
            />
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className={styles.statsSection}>
        <div className={styles.statsInner}>
          {STATS.map((stat) => (
            <div key={stat.label} className={styles.statItem}>
              <div className={styles.statValue}>{stat.value}</div>
              <Text variant="bodyLg" className={styles.statLabel}>
                {stat.label}
              </Text>
            </div>
          ))}
        </div>
      </section>

      {/* Wave: stats (lightBirch) → who it's for (white) */}
      <div className={styles.waveDividerLightBirchToWhite}>
        <svg
          viewBox="0 0 1440 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className={styles.waveSvg}
        >
          <path
            d="M0 80L1440 80L1440 20C1320 40 1200 60 1080 55C960 50 840 25 720 20C600 15 480 30 360 40C240 50 120 55 60 57.5L0 60L0 80Z"
            fill="white"
          />
        </svg>
      </div>

      {/* Who It's For (seller scenarios) */}
      <section className={styles.whoItsFor}>
        <div className={styles.whoItsForInner}>
          <div className={styles.sectionHeader}>
            <Text as="p" variant="label" className={styles.sectionEyebrow}>
              Who it's for
            </Text>
            <Text as="h2" variant="h2" align="center" className={styles.sectionTitle}>
              Why this matters for sellers today
            </Text>
            <Text variant="bodyLg" align="center" className={styles.sectionSubtitle}>
              When a traditional listing isn't the right answer, Orchard Offers
              gives your clients another way forward.
            </Text>
          </div>
          <div className={styles.sellerGrid}>
            {SELLER_SCENARIOS.map((scenario) => (
              <div key={scenario.label} className={styles.sellerItem}>
                <img src={scenario.icon} alt="" className={styles.sellerIcon} />
                <Text as="h3" weight="medium" className={styles.sellerLabel}>
                  {scenario.label}
                </Text>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className={styles.successSection}>
        <div className={styles.successInner}>
          <div className={styles.successHeader}>
            <Text as="p" variant="label" className={styles.successEyebrow}>
              Agent success stories
            </Text>
            <Text
              as="h2"
              variant="h2"
              align="center"
              className={`${styles.successTitle} ${styles.successTitleNoWrap}`}
            >
              Real deals, real sellers, real commission checks
            </Text>
          </div>
          <div className={styles.featuredStory}>
            <div className={styles.featuredQuote}>
              <Text as="p" variant="label" className={styles.featuredEyebrow}>
                {FEATURED_STORY.eyebrow}
              </Text>
              <div className={styles.featuredQuoteBlock}>
                <span className={styles.featuredQuoteMark}>“</span>
                <div className={styles.featuredQuoteContent}>
                  <p className={styles.featuredQuoteText}>{FEATURED_STORY.quote}”</p>
                  <Text className={styles.featuredAttribution}>
                    {FEATURED_STORY.attribution}
                  </Text>
                </div>
              </div>
            </div>
            <ol className={styles.featuredTimeline}>
              {FEATURED_STORY.timeline.map((step) => (
                <li key={step.day} className={styles.timelineRow}>
                  <div className={styles.timelineLabel}>
                    <Text as="span" className={styles.timelineDay}>
                      {step.day}
                    </Text>
                    <span
                      className={
                        step.highlight ? styles.timelineDotHighlight : styles.timelineDot
                      }
                      aria-hidden="true"
                    />
                  </div>
                  <div className={styles.timelineContent}>
                    <Text weight="medium" className={styles.timelineTitle}>
                      {step.title}
                    </Text>
                    <Text variant="bodyXs" className={styles.timelineBody}>
                      {step.body}
                    </Text>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className={styles.testimonialGrid}>
            {TESTIMONIALS.map((t) => (
              <div key={t.attribution + t.quote} className={styles.testimonialCard}>
                <FiveStars className={styles.testimonialStars} />
                <div className={styles.testimonialBody}>
                  <div className={styles.testimonialQuoteMark} aria-hidden="true">“</div>
                  <p className={styles.testimonialQuote}>{t.quote}”</p>
                  <Text className={styles.testimonialAttribution}>{t.attribution}</Text>
                </div>
              </div>
            ))}
          </div>
          <SubmitPropertyLink isAuthenticated={isAuthenticated}>
            <Button variant="primary" size="lg">
              Submit a property
            </Button>
          </SubmitPropertyLink>
        </div>
      </section>

      {/* Why Agents Use Us */}
      <section className={styles.whyAgents}>
        <div className={styles.whyAgentsInner}>
          <div className={styles.sectionHeader}>
            <Text as="p" variant="label" className={styles.sectionEyebrow}>
              Why agents use us
            </Text>
            <Text as="h2" variant="h2" align="center" className={styles.sectionTitle}>
              Give sellers more reasons to choose you
            </Text>
          </div>
          <div className={styles.whyAgentsGrid}>
            {WHY_AGENTS.map((item) => (
              <div key={item.title} className={styles.whyAgentsCard}>
                <Text as="h3" weight="medium" className={styles.whyAgentsCardTitle}>
                  {item.title}
                </Text>
                <Text variant="body" className={styles.whyAgentsCardBody}>
                  {item.body}
                </Text>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wave: why agents (lightBirch) → how it works (white) */}
      <div className={styles.waveDividerLightBirchToWhite}>
        <svg
          viewBox="0 0 1440 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className={styles.waveSvg}
        >
          <path
            d="M0 80L1440 80L1440 25C1380 35 1260 50 1140 45C1020 40 900 20 780 25C660 30 540 50 420 45C300 40 180 30 60 35L0 40L0 80Z"
            fill="white"
          />
        </svg>
      </div>

      {/* How It Works */}
      <section className={styles.howItWorks} id="how-it-works">
        <div className={styles.howItWorksInner}>
          <div className={styles.sectionHeader}>
            <Text as="p" variant="label" className={styles.sectionEyebrow}>
              How it works
            </Text>
            <Text as="h2" variant="h2" align="center" className={styles.sectionTitle}>
              From one submission to a signed contract
            </Text>
            <Text variant="bodyLg" align="center" className={styles.sectionSubtitle}>
              We do the heavy lifting on the buyer side. You stay the agent of
              record, the sole point of contact for your client, and keep your
              full commission.
            </Text>
          </div>
          <div className={styles.stepsGrid}>
            {STEPS.map((step, idx) => (
              <div key={step.title} className={styles.stepCard}>
                <div className={styles.stepNumber}>{idx + 1}</div>
                <Text as="h3" weight="medium" className={styles.stepTitle}>
                  {step.title}
                </Text>
                <Text variant="body" className={styles.stepBody}>
                  {step.body}
                </Text>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Markets Section — same white bg as How It Works, no divider between */}
      <section className={styles.marketsSection}>
        <div className={styles.marketsInner}>
          <div className={styles.sectionHeader}>
            <Text as="h2" variant="h2" align="center" className={styles.sectionTitle}>
              Markets we serve
            </Text>
            <Text variant="bodyLg" align="center" className={styles.sectionSubtitle}>
              Access off-market properties across {ACTIVE_MARKET_COUNT} growing markets.
            </Text>
          </div>
          <div className={styles.marketsLayout}>
            <div className={styles.mapContainer}>
              <img
                src="/images/us-map.svg"
                alt="US map showing Orchard Offers markets"
                className={styles.mapImage}
              />
              {MARKETS.map((market) => (
                <div
                  key={`marker-${market.city}-${market.state}`}
                  className={market.active ? styles.mapMarker : styles.mapMarkerInactive}
                  style={{ left: `${market.x}%`, top: `${market.y}%` }}
                  title={`${market.city}, ${market.state}${market.active ? '' : ' (coming soon)'}`}
                />
              ))}
            </div>
            <div className={styles.marketsListContainer}>
              <div className={styles.marketsList}>
                <Text as="h3" variant="label" className={styles.marketsListTitle}>
                  Active Markets
                </Text>
                {MARKETS.filter((m) => m.active).map((market) => (
                  <Text
                    key={`${market.city}-${market.state}`}
                    variant="bodySm"
                    className={styles.marketItem}
                  >
                    {market.city}, {market.state}
                  </Text>
                ))}
              </div>
              <div className={styles.marketsList}>
                <Text
                  as="h3"
                  variant="label"
                  color="muted"
                  className={styles.marketsListTitleMuted}
                >
                  Coming Soon
                </Text>
                {MARKETS.filter((m) => !m.active).map((market) => (
                  <Text
                    key={`${market.city}-${market.state}`}
                    variant="bodySm"
                    color="muted"
                    className={styles.marketItemMuted}
                  >
                    {market.city}, {market.state}
                  </Text>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wave: markets (white) → CTA (skyLight) */}
      <div className={styles.waveDividerWhiteToSkyLight}>
        <svg
          viewBox="0 0 1440 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className={styles.waveSvg}
        >
          <path
            d="M0 80L1440 80L1440 35C1350 20 1260 40 1140 50C1020 60 900 45 780 35C660 25 540 35 420 45C300 55 180 45 60 40L0 35L0 80Z"
            fill="#EDFBFD"
          />
        </svg>
      </div>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <Text as="h2" variant="h2" align="center" className={styles.ctaTitle}>
            Ready to submit a home to Orchard Offers?
          </Text>
          <Text variant="bodyLg" align="center" className={styles.ctaSubtitle}>
            Help your client get multiple cash offers and guide them to the best one.
          </Text>
          <SubmitPropertyLink isAuthenticated={isAuthenticated}>
            <Button variant="primary" size="lg">
              Submit a property
            </Button>
          </SubmitPropertyLink>
          <Text variant="body" className={styles.ctaInvestorNote}>
            Are you an investor? Visit the{' '}
            <Link to="/investors" className={styles.ctaInvestorLink}>
              investor site
            </Link>
            .
          </Text>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <Text variant="h4" align="center" className={styles.footerContactHeading}>
          Questions?
        </Text>
        <Text variant="bodyLg" align="center" className={styles.footerContactSubheading}>
          We're here to help.
        </Text>
        <a href="mailto:support@orchardoffers.com" className={styles.footerContactLink}>
          support@orchardoffers.com
        </a>
      </footer>
    </div>
  )
}
